/**
 * feat-289 — dummy data for the crew call-out review queue.
 *
 * `interviewer-frontend/CLAUDE.md`: *"`npm run start:fake` must work with no
 * backend running. When you add a view, give it dummy data."* This is that.
 *
 * ⚠️ READ THIS BEFORE TRUSTING IT AS A DEMO PATH. `config.useDummyData` is
 * `false` in **all four** environments today (`config/environment.ts` — fake,
 * local, dev and prod), so nothing below currently renders anywhere, and
 * `start:fake` still requires a real Cognito session and therefore a real
 * backend to reach an `<AdminRoute>` page at all (feat-208). It is written and
 * kept accurate anyway for two reasons: it is the fixture the queue's own tests
 * render against, so it cannot rot unnoticed; and the day feat-208 flips that
 * flag the view has a demo path already.
 *
 * Every row here is a SHAPE the queue must handle, not decoration:
 *   1. a machine-written draft — the ordinary job, check it and approve;
 *   2. a HELD draft (D10/AC31) — every generation step failed, `line_en` is null,
 *      the reviewer writes both lines from the verbatim source;
 *   3. an approved row, edited by a human (`edited: true`);
 *   4. an approved row the machine got right (`edited: false`), already
 *      delivered to a phone;
 *   5. a rejected row.
 *
 * feat-322 adds the source chain, so the fixture also carries:
 *   6. a draft written from a FALLBACK highlight (`highlight_fallback`);
 *   7. a draft generated from the TRANSCRIPT, with a paraphrased `basis`;
 * and the held row now records its `hold_reasons` and best `basis`. Row 4 is
 * left with no `source_kind` at all — the legacy (pre-feat-322) shape.
 *
 * `no_candidate_count` is deliberately larger than `rows.length`: the fallback
 * population is the number that tells Bespoke whether the pipeline is working,
 * and a fixture where it was small would make the point invisible in exactly
 * the surface built to make it visible.
 */

import type {
  CalloutRow,
  CalloutWeek,
  CalloutWeekSummary,
} from './crewCalloutsAdminService';

const KAPOLEI = { location_id: 'loc-kapolei', location_name: 'L&L Kapolei' };
const PEARL = { location_id: 'loc-pearl', location_name: 'L&L Pearl City' };

const WEEK = '2026-09-14';

const ROWS: CalloutRow[] = [
  {
    id: 'callout-draft-1',
    server_id: 'srv-marisol',
    first_name: 'Marisol',
    ...KAPOLEI,
    week_start: WEEK,
    event_date: '2026-09-16',
    source_shift_id: 'shift-4821',
    source_interaction_id: 'int-90311',
    source_title: 'Offered the Dubai chocolate to a table that had already ordered dessert',
    source_kind: 'highlight',
    basis: null,
    hold_reasons: null,
    line_en:
      'On Wednesday you offered the Dubai chocolate to a table that had already ordered — nice read of the room.',
    line_es:
      'El miércoles ofreciste el chocolate de Dubái a una mesa que ya había pedido: buena lectura del momento.',
    status: 'draft',
    reviewed_by: null,
    reviewed_at: null,
    edited: false,
    created: '2026-09-14T15:02:00Z',
    delivered_at: null,
  },
  {
    // The held row. `line_en` is null and that is a REAL state, not a bug.
    id: 'callout-draft-held',
    server_id: 'srv-devon',
    first_name: 'Devon',
    ...PEARL,
    week_start: WEEK,
    event_date: '2026-09-15',
    source_shift_id: 'shift-4799',
    source_interaction_id: 'int-90144',
    source_title: 'Greeted a party of six before they reached the counter',
    source_kind: 'highlight',
    basis: 'Greeted a party of six before they reached the counter',
    hold_reasons: [
      'highlight 1, attempt 1: es: 240 characters — a paragraph, not a line',
      'highlight 1, attempt 2: es: 231 characters — a paragraph, not a line',
      'highlight 1, attempt 3: en: shares 5+ consecutive words with the source',
      'transcript: no praiseworthy crew-member moment found',
    ],
    line_en: null,
    line_es: null,
    status: 'draft',
    reviewed_by: null,
    reviewed_at: null,
    edited: false,
    created: '2026-09-14T15:02:00Z',
    delivered_at: null,
  },
  {
    id: 'callout-approved-edited',
    server_id: 'srv-kai',
    first_name: 'Kai',
    ...KAPOLEI,
    week_start: WEEK,
    event_date: '2026-09-17',
    source_shift_id: 'shift-4855',
    source_interaction_id: null,
    source_title: 'Walked a first-time guest through the plate sizes',
    source_kind: 'transcript',
    basis: 'Walked a first-time guest through the plate sizes and let them take their time choosing',
    hold_reasons: null,
    line_en: 'On Thursday you walked a first-timer through the plate sizes without rushing them.',
    line_es: 'El jueves guiaste a un cliente nuevo por los tamaños de plato sin apurarlo.',
    status: 'approved',
    reviewed_by: 'preston@bespokecast.ai',
    reviewed_at: '2026-09-15T16:41:00Z',
    edited: true,
    created: '2026-09-14T15:02:00Z',
    delivered_at: null,
  },
  {
    id: 'callout-approved-delivered',
    server_id: 'srv-alani',
    first_name: 'Alani',
    ...PEARL,
    week_start: WEEK,
    event_date: '2026-09-14',
    source_shift_id: 'shift-4770',
    source_interaction_id: 'int-90008',
    source_title: 'Offered the steak shawarma upsell on a slow Monday',
    line_en: 'On Monday you offered the steak shawarma when the line was quiet — easy to skip, and you did not.',
    line_es: 'El lunes ofreciste el shawarma de bistec cuando había poca gente: fácil de saltarse, y no lo hiciste.',
    status: 'approved',
    reviewed_by: 'preston@bespokecast.ai',
    reviewed_at: '2026-09-15T16:39:00Z',
    edited: false,
    created: '2026-09-14T15:02:00Z',
    delivered_at: '2026-09-15T17:22:00Z',
  },
  {
    id: 'callout-rejected',
    server_id: 'srv-tomas',
    first_name: 'Tomás',
    ...KAPOLEI,
    week_start: WEEK,
    event_date: '2026-09-18',
    source_shift_id: 'shift-4901',
    source_interaction_id: 'int-90702',
    source_title: 'Re-offered a drink after the guest declined',
    source_kind: 'highlight',
    basis: null,
    hold_reasons: null,
    line_en: 'On Friday you kept offering the drink until the guest said yes.',
    line_es: 'El viernes seguiste ofreciendo la bebida hasta que el cliente aceptó.',
    status: 'rejected',
    reviewed_by: 'preston@bespokecast.ai',
    reviewed_at: '2026-09-15T16:44:00Z',
    edited: false,
    created: '2026-09-14T15:02:00Z',
    delivered_at: null,
  },
  {
    id: 'callout-draft-fallback',
    server_id: 'srv-noelani',
    first_name: 'Noelani',
    ...PEARL,
    week_start: WEEK,
    event_date: '2026-09-17',
    source_shift_id: 'shift-4862',
    source_interaction_id: 'int-90577',
    source_title: 'Suggested a half-and-half plate to a guest who could not decide',
    source_kind: 'highlight_fallback',
    basis: null,
    hold_reasons: null,
    line_en:
      'On Thursday you helped an undecided guest land on a half-and-half plate, and they left happy.',
    line_es:
      'El jueves ayudaste a un cliente indeciso a elegir un plato mitad y mitad, y se fue contento.',
    status: 'draft',
    reviewed_by: null,
    reviewed_at: null,
    edited: false,
    created: '2026-09-14T15:02:00Z',
    delivered_at: null,
  },
  {
    // Generated from the transcript: no interaction, `basis` is a paraphrase.
    id: 'callout-draft-transcript',
    server_id: 'srv-rafael',
    first_name: 'Rafael',
    ...KAPOLEI,
    week_start: WEEK,
    event_date: '2026-09-15',
    source_shift_id: 'shift-4802',
    source_interaction_id: null,
    source_title: 'Checked back on a guest whose order was delayed and offered a fresh drink',
    source_kind: 'transcript',
    basis: 'Checked back on a guest whose order was delayed and offered a fresh drink',
    hold_reasons: null,
    line_en:
      'On Tuesday you checked back on a guest whose order ran late and kept the wait easy for them.',
    line_es:
      'El martes volviste con un cliente cuyo pedido se atrasó e hiciste la espera más llevadera.',
    status: 'draft',
    reviewed_by: null,
    reviewed_at: null,
    edited: false,
    created: '2026-09-14T15:02:00Z',
    delivered_at: null,
  },
];

export function fakeCalloutWeek(
  weekStart?: string,
  locationId?: string
): CalloutWeek {
  const rows = locationId
    ? ROWS.filter((r) => r.location_id === locationId)
    : ROWS;
  return {
    week_start: weekStart || WEEK,
    week_end: '2026-09-18',
    location_id: locationId ?? null,
    draft_count: rows.filter((r) => r.status === 'draft').length,
    // Far larger than `rows.length`, on purpose — see the file header.
    no_candidate_count: locationId ? 7 : 19,
    rows: rows.map((r) => ({ ...r, week_start: weekStart || WEEK })),
  };
}

export function fakeCalloutWeeks(limit = 8): CalloutWeekSummary[] {
  const all: CalloutWeekSummary[] = [
    { week_start: '2026-09-14', drafts: 4, approved: 2, rejected: 1 },
    { week_start: '2026-09-07', drafts: 0, approved: 9, rejected: 1 },
    { week_start: '2026-08-31', drafts: 0, approved: 7, rejected: 0 },
    { week_start: '2026-08-24', drafts: 0, approved: 11, rejected: 2 },
  ];
  return all.slice(0, limit);
}
